import { Scene } from 'phaser';
import { Enemy } from '../entities/Enemy';
import { EnemyType } from '../config/BalanceConfig';

export class SpawnSystem {
  private scene: Scene;
  private baseSpawnInterval: number = 2000;
  private currentSpawnInterval: number;
  private spawnTimer!: Phaser.Time.TimerEvent;
  private waveDifficulty: number = 1;

  constructor(scene: Scene) {
    this.scene = scene;
    this.currentSpawnInterval = this.baseSpawnInterval;
  }

  public start(): void {
    this.spawnTimer = this.scene.time.addEvent({
      delay: this.currentSpawnInterval,
      callback: () => this.spawnEnemy(),
      repeat: -1
    });
  }

  public stop(): void {
    if (this.spawnTimer) {
      this.spawnTimer.remove();
    }
  }

  public increaseDifficulty(waveNumber: number): void {
    this.waveDifficulty = waveNumber;
    this.currentSpawnInterval = Math.max(500, this.baseSpawnInterval - (waveNumber * 100));
    
    // Restart spawn timer with new interval
    this.stop();
    this.start();
  }

  private spawnEnemy(): void {
    const { width } = this.scene.cameras.main;
    const x = Phaser.Math.Between(50, width - 50);
    const y = 30;

    const enemyType = this.selectEnemyType();
    const enemy = new Enemy(this.scene, x, y, enemyType);
    
    // Apply difficulty scaling
    this.applyDifficultyScaling(enemy);
    
    this.scene.events.emit('enemy-spawned', enemy);
  }

  public onEnemySpawned(): void {
    // Called when an enemy is spawned for tracking purposes
  }

  private selectEnemyType(): EnemyType {
    // As difficulty increases, more ranged enemies spawn
    const rangedChance = Math.min(0.6, 0.3 + (this.waveDifficulty * 0.05));
    
    return Math.random() < rangedChance ? EnemyType.RANGED : EnemyType.MELEE;
  }

  private applyDifficultyScaling(enemy: Enemy): void {
    const stats = enemy.getStats();
    
    // Scale stats with wave difficulty
    const healthMultiplier = 1 + (this.waveDifficulty * 0.1);
    const damageMultiplier = 1 + (this.waveDifficulty * 0.05);
    
    // Apply scaling (this would need to be implemented in Enemy class)
    // For now, we'll just emit an event with scaling data
    this.scene.events.emit('apply-difficulty-scaling', {
      enemy,
      healthMultiplier,
      damageMultiplier
    });
  }

  public spawnWave(enemyCount: number): void {
    for (let i = 0; i < enemyCount; i++) {
      this.scene.time.delayedCall(i * 200, () => {
        this.spawnEnemy();
      });
    }
  }

  public getCurrentSpawnInterval(): number {
    return this.currentSpawnInterval;
  }

  public getWaveDifficulty(): number {
    return this.waveDifficulty;
  }
}